import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { applyMembershipChanges, createLimiter } from '../../app/election-parameters/ballot/ballot-helpers';
import type { IApiResponse } from '../../helpers/types/general';
import type {
	BallotSource,
	IBallotParties,
	ICycleParties,
	ICyclePartyInput,
	IPartyCycle,
	IPartyMatrix,
	ISetCyclePartiesResult,
} from '../../interface/state/IBallotState';
import type { IElectionState, IElectionStates } from '../../interface/state/IElectionState';
import type { IPartyState } from '../../interface/state/IPartyState';
import { coreApi } from '../../lib/api-client';
import { ballotKeys } from './ballot-keys';
import { useActiveParties } from './use-party-queries';

// Every call goes through `coreApi` with the caller's own try/catch — never Redux, whose
// `onFailure` never fires and whose cancel tokens make parallel same-type calls cancel each other.

// ─── Fetchers (exported so non-hook code can await them) ────────────────────

export const fetchCycleParties = async (cycleId: number): Promise<ICycleParties> => {
	const { data } = await coreApi.get<IApiResponse<ICycleParties>>(`/api/v1/ElectionCycle/${cycleId}/parties`);
	const body = data?.data;
	return {
		electionCycleId: body?.electionCycleId ?? cycleId,
		electionCycleName: body?.electionCycleName ?? '',
		totalCount: body?.totalCount ?? body?.parties?.length ?? 0,
		parties: Array.isArray(body?.parties) ? body.parties : [],
	};
};

export const putCycleParties = async (
	cycleId: number,
	parties: ICyclePartyInput[],
	force = false
): Promise<ISetCyclePartiesResult> => {
	const { data } = await coreApi.put<IApiResponse<ISetCyclePartiesResult>>(
		`/api/v1/ElectionCycle/${cycleId}/parties${force ? '?force=true' : ''}`,
		{ parties }
	);
	return data.data;
};

export const copyCycleParties = async (cycleId: number, sourceCycleId: number, force = false) => {
	const { data } = await coreApi.post<IApiResponse<ISetCyclePartiesResult>>(
		`/api/v1/ElectionCycle/${cycleId}/parties/copy-from/${sourceCycleId}${force ? '?force=true' : ''}`
	);
	return data.data;
};

/**
 * Adds and/or removes parties on one attribute without disturbing anyone else.
 *
 * The PUT replaces the whole set, so the roster is re-read immediately before writing: a cached
 * copy could silently undo a change someone made in another tab.
 */
export const updateCycleMembership = async (
	cycleId: number,
	changes: { add?: number[]; remove?: number[] },
	force = false
): Promise<ISetCyclePartiesResult> => {
	const fresh = await fetchCycleParties(cycleId);
	return putCycleParties(cycleId, applyMembershipChanges(fresh.parties, changes), force);
};

const fetchPartyCycles = async (partyId: number): Promise<IPartyCycle[]> => {
	const { data } = await coreApi.get<IApiResponse<IPartyCycle[]>>(`/api/v1/Party/${partyId}/cycles`);
	return Array.isArray(data?.data) ? data.data : [];
};

const fetchPartyMatrix = async (filters: { electionTypeId?: number; activeOnly?: boolean }): Promise<IPartyMatrix> => {
	const params = new URLSearchParams();
	if (filters.electionTypeId) params.set('electionTypeId', String(filters.electionTypeId));
	if (filters.activeOnly) params.set('activeOnly', 'true');
	const qs = params.toString();
	const { data } = await coreApi.get<IApiResponse<IPartyMatrix>>(`/api/v1/ElectionCycle/party-matrix${qs ? `?${qs}` : ''}`);
	return {
		cycles: data?.data?.cycles ?? [],
		parties: data?.data?.parties ?? [],
		cells: data?.data?.cells ?? [],
	};
};

// Per-election resolver reads fan out from the Elections tab; four at a time keeps a 40-election
// governorship cycle from firing 40 requests at once.
const ballotLimiter = createLimiter(4);

/**
 * `null` means "no usable answer" — an older api.core without the route, a failure, or a body
 * without `parties`. Callers treat that as the legacy behaviour (all active parties), never as
 * an empty ballot: an empty list would render a result form with no rows.
 */
export const fetchBallotParties = async (electionId: number): Promise<IBallotParties | null> => {
	try {
		const { data } = await ballotLimiter(() =>
			coreApi.get<IApiResponse<IBallotParties>>(`/api/v1/Election/${electionId}/ballot-parties`)
		);
		const body = data?.data;
		if (!body || !Array.isArray(body.parties)) return null;
		return {
			electionId: body.electionId ?? electionId,
			electionCycleId: body.electionCycleId ?? null,
			source: body.source ?? 'fallback',
			parties: body.parties,
		};
	} catch {
		return null;
	}
};

export const ballotPartiesQueryOptions = (electionId: number) =>
	queryOptions({
		queryKey: ballotKeys.ballotParties(electionId),
		queryFn: () => fetchBallotParties(electionId),
		staleTime: 60_000,
	});

const fetchCycleElections = async (cycleId: number): Promise<IElectionState[]> => {
	const out: IElectionState[] = [];
	// PageSize is clamped to 100 server-side; walk pages, but stop at 10 (1,000 elections).
	for (let page = 1; page <= 10; page++) {
		const { data } = await coreApi.get<IElectionStates>(
			`/api/v1/Election/paged?ElectionCycleId=${cycleId}&PageSize=100&PageNumber=${page}`
		);
		const rows = Array.isArray(data?.data) ? data.data : [];
		out.push(...rows);
		if (rows.length < 100 || page >= (data?.totalPages ?? 1)) break;
	}
	// Re-check the cycle client-side: an ignored filter returns HTTP 200 with every election.
	return out.filter((e) => e.electionCycleId === undefined || Number(e.electionCycleId) === cycleId);
};

export const putElectionBallot = async (electionId: number, partyIds: number[]) => {
	const { data } = await coreApi.put<IApiResponse<unknown>>('/api/v1/Party/active-parties', { electionId, partyIds });
	return data;
};

// ─── Queries ────────────────────────────────────────────────────────────────

export function useCycleParties(cycleId: number | undefined, options: { enabled?: boolean } = {}) {
	return useQuery({
		queryKey: ballotKeys.cycle(cycleId ?? 0),
		queryFn: () => fetchCycleParties(cycleId!),
		enabled: (options.enabled ?? true) && !!cycleId,
	});
}

export function usePartyCycles(partyId: number | undefined) {
	return useQuery({
		queryKey: ballotKeys.partyCycles(partyId ?? 0),
		queryFn: () => fetchPartyCycles(partyId!),
		enabled: !!partyId,
	});
}

export function usePartyMatrix(filters: { electionTypeId?: number; activeOnly?: boolean } = {}, enabled = true) {
	return useQuery({
		queryKey: ballotKeys.matrix(filters),
		queryFn: () => fetchPartyMatrix(filters),
		enabled,
	});
}

export function useBallotParties(electionId: number | undefined, options: { enabled?: boolean } = {}) {
	return useQuery({
		...ballotPartiesQueryOptions(electionId ?? 0),
		enabled: (options.enabled ?? true) && !!electionId && electionId > 0,
	});
}

export function useCycleElections(cycleId: number | undefined, enabled = true) {
	return useQuery({
		queryKey: ballotKeys.cycleElections(cycleId ?? 0),
		queryFn: () => fetchCycleElections(cycleId!),
		enabled: enabled && !!cycleId,
	});
}

/** Every party, active or not — the transfer list greys inactive ones rather than hiding them. */
export function useAllParties(enabled = true) {
	return useQuery({
		queryKey: ballotKeys.allParties(),
		queryFn: async (): Promise<IPartyState[]> => {
			const { data } = await coreApi.get<IApiResponse<IPartyState[]>>('/api/v1/Party');
			return Array.isArray(data?.data) ? data.data : [];
		},
		enabled,
		staleTime: 5 * 60_000,
	});
}

export function useElectionTypes() {
	return useQuery({
		queryKey: ballotKeys.electionTypes(),
		queryFn: async (): Promise<{ id: number; name: string }[]> => {
			// api.core returns this list BARE (`Ok(list)`, no envelope). Reading only `.data` left every
			// category unnamed ("Category 1"); accept both so an envelope added later keeps working.
			const { data } = await coreApi.get<{ id: number; name: string }[] | IApiResponse<{ id: number; name: string }[]>>(
				'/api/v1/ElectionType'
			);
			if (Array.isArray(data)) return data;
			return Array.isArray(data?.data) ? data.data : [];
		},
		staleTime: 10 * 60_000,
	});
}

export interface IEffectiveBallot {
	parties: IPartyState[];
	/** `legacy`: resolver unavailable, so the pre-feature list (all active parties) is shown. */
	source: BallotSource | 'legacy';
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
}

/** Ballot order first; unordered parties keep the server's order after them. */
const byBallotOrder = <T extends { ballotOrder: number | null }>(parties: T[]): T[] =>
	parties
		.map((p, i) => ({ p, i }))
		.sort((a, b) => (a.p.ballotOrder ?? Number.MAX_SAFE_INTEGER) - (b.p.ballotOrder ?? Number.MAX_SAFE_INTEGER) || a.i - b.i)
		.map(({ p }) => p);

/**
 * The read every Phase 1 consumer uses: the election's effective ballot, or — when the resolver
 * has no answer — the same active-party list the screen showed before this feature existed.
 */
export function useEffectiveBallot(electionId: number | undefined, options: { enabled?: boolean } = {}): IEffectiveBallot {
	const enabled = options.enabled ?? true;
	const ballot = useBallotParties(electionId, { enabled });
	const needsLegacy = enabled && (!electionId || (ballot.isFetched && ballot.data === null));
	const active = useActiveParties({ enabled: needsLegacy });

	if (needsLegacy) {
		return {
			parties: active.data ?? [],
			source: 'legacy',
			isLoading: active.isLoading,
			isError: active.isError,
			refetch: () => void active.refetch(),
		};
	}
	return {
		parties: byBallotOrder(ballot.data?.parties ?? []),
		source: ballot.data?.source ?? 'legacy',
		isLoading: ballot.isLoading,
		isError: false,
		refetch: () => void ballot.refetch(),
	};
}

// ─── Mutations ──────────────────────────────────────────────────────────────

const useInvalidateBallot = () => {
	const queryClient = useQueryClient();
	// The root covers cycle, matrix, party-cycles and every ballot-parties entry: one write to an
	// attribute changes what each of its elections inherits.
	return () => queryClient.invalidateQueries({ queryKey: ballotKeys.all });
};

export function useSetCycleParties() {
	const invalidate = useInvalidateBallot();
	return useMutation({
		mutationFn: ({ cycleId, parties, force }: { cycleId: number; parties: ICyclePartyInput[]; force?: boolean }) =>
			putCycleParties(cycleId, parties, force),
		onSuccess: () => invalidate(),
	});
}

export function useCopyCycleParties() {
	const invalidate = useInvalidateBallot();
	return useMutation({
		mutationFn: ({ cycleId, sourceCycleId, force }: { cycleId: number; sourceCycleId: number; force?: boolean }) =>
			copyCycleParties(cycleId, sourceCycleId, force),
		onSuccess: () => invalidate(),
	});
}

export function useSetElectionBallot() {
	const invalidate = useInvalidateBallot();
	return useMutation({
		mutationFn: ({ electionId, partyIds }: { electionId: number; partyIds: number[] }) => putElectionBallot(electionId, partyIds),
		onSuccess: () => invalidate(),
	});
}

/** For callers that run several writes in sequence and invalidate once at the end. */
export const useBallotInvalidation = useInvalidateBallot;
